import { useCallback, useState } from 'react'
import { useMutation } from '@apollo/client/react'
import axios from 'axios'
import type { ApiError } from '@pbt/pbt-ui-components'

import * as API from '~/api'
import {
  CreateManualAttachmentFilesDocument,
  ManualAttachmentFileType,
} from '~/api/graphql/generated/types'
import AssetDestination from '~/constants/AssetDestination'
import type { UploadAssetPayload } from '~/types'
import { getErrorMessage } from '~/utils/errors'
import { mapFilesToObjects } from '~/utils/file'

const fileContentTypeHeaders = (file: File) => ({
  'Content-Type': file.type,
})

export const useCreateManualAttachmentFiles = (
  businessId: string,
  clientId: string,
  patientId: string,
  orderId: string,
) => {
  const [createManualAttachmentFilesMutation, { error, reset }] = useMutation(
    CreateManualAttachmentFilesDocument,
  )

  const [loading, setLoading] = useState(false)

  const createAttachments = useCallback(
    async (files: File[]) => {
      if (!businessId || !clientId || !patientId || !orderId) {
        throw new Error('Missing required parameters')
      }

      try {
        setLoading(true)
        // Step 1: Reserve upload URLs for all files
        const uploadPayload: UploadAssetPayload = {
          personId: clientId,
          patientId,
          isPublic: false,
        }

        const reservePromises = files.map(() =>
          API.reserveAssetUpload(
            businessId,
            AssetDestination.PATIENT,
            uploadPayload,
          ),
        )
        const uploadResponses = await Promise.all(reservePromises)

        // Step 2: Upload files to S3
        const uploadPromises = uploadResponses.map((response, index) =>
          axios.put(response.uploadUrl, files[index], {
            headers: fileContentTypeHeaders(files[index]),
          }),
        )
        await Promise.all(uploadPromises)

        // Step 3: Extract file URLs
        const fileUrls = uploadResponses.map((response) => response.fileUrl)

        // Step 4: Map files to input objects
        const fileObjects = mapFilesToObjects(files, fileUrls, true)

        // Step 5: Create manual attachment files via GraphQL mutation
        const input = fileObjects.map((fileObj) => ({
          entityId: orderId,
          name: fileObj.name || '',
          originalFileName: fileObj.originalFileName,
          entityType: ManualAttachmentFileType.IdexxImaging,
          fileUrl: fileObj.fileUrl,
          extension: fileObj.extension || '',
          byteSize: fileObj.size,
        }))

        const result = await createManualAttachmentFilesMutation({
          variables: { input },
        })

        return result.data?.createManualAttachmentFiles || []
      } catch (err) {
        throw new Error(getErrorMessage(err as ApiError))
        /* v8 ignore next */
      } finally {
        setLoading(false)
      }
    },
    [
      businessId,
      clientId,
      patientId,
      orderId,
      createManualAttachmentFilesMutation,
    ],
  )

  return {
    createAttachments,
    loading,
    error,
    reset,
  }
}
